import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Phone, ArrowRight, Loader2, Shield, CheckCircle } from 'lucide-react';

const API = '/api';

export default function MobileVerifyPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // phone | otp
  const [loading, setLoading] = useState(false);
  const [maskedMsg, setMaskedMsg] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) return toast.error('Enter a valid 10-digit mobile number');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mobile-otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ mobile: phone })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setStep('otp');
      setMaskedMsg(data.message);
      toast.success('OTP sent to your mobile!');
    } catch (err) {
      toast.error(err.message || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mobile-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ mobile: phone, otp })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      updateUser(data.user || data);
      toast.success('Mobile verified successfully!');
      navigate(user?.role === 'tenant' ? '/search' : '/dashboard');
    } catch (err) {
      toast.error(err.message || 'Invalid OTP');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Verify Mobile | Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{fontFamily:'Outfit,sans-serif'}}>
            Verify Your Mobile
          </h1>
          <p className="text-slate-500 text-sm">Required to contact owners and list properties</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-6 px-2">
          <div className="flex items-center gap-1.5 flex-1">
            <div className="w-7 h-7 rounded-full bg-blue-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
            <div className={`h-1 flex-1 rounded-full transition-colors ${step === 'otp' ? 'bg-blue-700' : 'bg-slate-200'}`}></div>
          </div>
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${step === 'otp' ? 'bg-blue-700 text-white' : 'bg-slate-200 text-slate-500'}`}>2</div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-[0_8px_40px_rgb(0,0,0,0.08)] border border-slate-100 p-8">
          {step === 'phone' ? (
            <form onSubmit={handleSendOtp} className="space-y-5" data-testid="mobile-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile Number</label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 flex-shrink-0">
                    <span className="text-base">🇮🇳</span> +91
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
                    placeholder="9876543210"
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 font-semibold tracking-widest"
                    data-testid="mobile-input"
                    required autoFocus
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">We'll send a 6-digit OTP to verify your number</p>
              </div>
              <button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="send-mobile-otp-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Sending OTP...' : 'Send OTP via SMS'}
              </button>
              <button
                type="button"
                onClick={() => navigate(user?.role === 'tenant' ? '/search' : '/dashboard')}
                className="w-full text-slate-400 text-sm hover:text-slate-600 transition-colors py-1"
                data-testid="skip-mobile-button"
              >
                Skip for now
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="mobile-otp-form">
              <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex gap-3 items-start">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-900">{maskedMsg}</p>
                  <p className="text-xs text-green-600 mt-0.5">Enter the 6-digit OTP below</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Enter OTP</label>
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="• • • • • •"
                  className="w-full px-4 py-4 border border-slate-200 rounded-xl text-2xl font-bold tracking-[0.5em] text-center outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="mobile-otp-input"
                  maxLength={6} autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="verify-mobile-otp-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                {loading ? 'Verifying...' : 'Verify Mobile'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); }}
                className="w-full text-slate-400 text-sm hover:text-slate-600 transition-colors"
              >
                Change number
              </button>
            </form>
          )}
        </div>

        {/* Info note */}
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-2.5 items-start">
          <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Your verified mobile number helps tenants contact you directly and builds trust. It will be displayed on your property listings.
          </p>
        </div>
      </div>
    </div>
  );
}
